import { useEffect, useRef, useState } from 'preact/hooks';
import { useAccount } from './Dashboard';
import { dashboardApi } from './dashboardApi';
import { BillingToggle, PAID_TIER_ORDER, PLANS, PlanCards, type Billing, type CurrentPlan } from './pricingPlans';
import { RemoveBadgeCard } from './BillingUsage';
import { PrivacyFacts } from './Privacy';

// ============================================================================
// Pricing — the /pricing page. Plan data, the
// toggle, and the card grid all live in pricingPlans.tsx (plan data shared with
// Dashboard.tsx); this file is just that section's own
// heading/copy plus the one thing specific to a public marketing
// visitor — hiding Free once an account has opted into any plan.
//
// Free shows in exactly two cases: signed out, or signed in with level
// 'none' (never selected a plan yet). Once an account has opted into
// something — the free trial itself included, not just a paid plan —
// Free never shows again. This is also where every "Upgrade" button in
// the dashboard (Dashboard.tsx's UPGRADE_URL) sends an existing customer,
// so nobody already on (or past) the trial can click their way back to
// a second free option.

// The questions people ask right before paying. Every answer is checked
// against what the product does — the limit handling in
// server/src/billing/allowance.ts, cancellation and plan changes in
// server/src/billing/ and the dashboard's Billing page, re-crawls in
// server/src/crawl/weekly.ts, the grounding and no-legal-advice rules in
// server/src/prompt/compose.ts — so change those and this follows.
const PRICING_FAQS: { q: string; a: preact.ComponentChildren }[] = [
  {
    q: 'What happens if my chatbots use up their messages?',
    a: (
      <>
        They stop answering and show your contact form instead, so visitors can still leave their details and you get every lead by
        email. We email you at 80% of the month's messages and again at 100%. They start answering again on the 1st of the month, or
        straight away if you upgrade.
      </>
    ),
  },
  {
    q: 'Can I cancel? Do I get a refund?',
    a: (
      <>
        Yes, cancel any time from Manage billing on your Billing page. Your plan keeps working until the end of the period you've already
        paid for, and you won't be charged again. We don't refund payments already made, including for the unused part of a month or
        year.
      </>
    ),
  },
  {
    q: 'What happens to my chatbot and content after I cancel?',
    a: (
      <>
        Nothing is deleted when your plan ends: your chatbots stop answering, and your content and settings stay in your account, so
        choosing a plan again picks up where you left off. Conversations, and the contact details that came with them, are still
        deleted on the schedule you set. You can delete a chatbot yourself at any time, or ask us to delete everything — see the{' '}
        <a href="/privacy">privacy policy</a>.
      </>
    ),
  },
  {
    q: 'How much work is installation?',
    a: (
      <>
        You paste one line of code into your site, the way you'd add analytics, so it works anywhere you can add a script tag. Then you
        verify that you own the domain, with the same snippet, a meta tag or a file, so the assistant only runs on your site. Most
        people are set up in a few minutes.
      </>
    ),
  },
  {
    q: 'Where do the answers come from? Will it give legal advice?',
    a: (
      <>
        Only from the pages and files you give it. If the answer isn't there, it says so instead of guessing, and can offer your
        contact form so someone follows up. It doesn't give legal advice: it can repeat what your content says, but suggests visitors
        speak to a qualified professional about their own situation.
      </>
    ),
  },
  {
    q: 'Where does conversation data go? Is it used to train AI?',
    a: (
      <>
        Conversations are stored in our database on Cloudflare, and sent to OpenAI to write each answer. OpenAI doesn't train its
        models on data sent through its API. The <a href="/privacy">privacy policy</a> covers what's kept, where it's kept and for how
        long.
      </>
    ),
  },
  {
    q: 'Can I change plans later?',
    a: (
      <>
        Yes, upgrade or downgrade any time from your Billing page. An upgrade charges only the difference for the rest of your billing
        period and a downgrade credits the unused part, so you're never billed for two plans. If the new plan allows fewer chatbots,
        your existing ones keep working, but you can't add one until you're within the limit.
      </>
    ),
  },
  {
    q: 'My website changed. Does the assistant keep up?',
    a: (
      <>
        Yes. Your site is re-read every month on Starter and every week on Professional and Business, and you can rescan it from the
        dashboard at any time, the free trial included. An answer you save yourself takes effect the moment you save it.
      </>
    ),
  },
];

function PricingFaq() {
  return (
    <div class="pc-faq">
      <h3 class="pv-pricing-title">Questions before you buy</h3>
      <div class="site-faq">
        {PRICING_FAQS.map((item, i) => (
          <details key={item.q} style={{ '--i': i }}>
            <summary>{item.q}</summary>
            <p>{item.a}</p>
          </details>
        ))}
      </div>
    </div>
  );
}

export function Pricing() {
  const [billing, setBilling] = useState<Billing>('yearly');
  const account = useAccount();
  // null covers signed-out (the common case) and "haven't loaded yet" —
  // both show Free, same as before this existed.
  const hideFree = !!account && account.level !== 'none';
  const plans = hideFree ? PLANS.filter((p) => p.id !== 'free') : PLANS;

  // A paying customer (the account menu's "Upgrade" lands here) sees their
  // own plan marked, and the others as upgrades or downgrades. How they're
  // billed comes from the payment provider; until it answers the card is
  // still marked, and once it does the toggle opens on their cycle.
  const paidLevel = account && (PAID_TIER_ORDER as readonly string[]).includes(account.level) ? account.level : null;
  const [cycle, setCycle] = useState<Billing | null>(null);
  // Set once the visitor flips the toggle themselves: the provider's answer
  // can take seconds, and must not flip it back under them when it lands.
  const choseBilling = useRef(false);
  const chooseBilling = (b: Billing) => {
    choseBilling.current = true;
    setBilling(b);
  };
  // Whether the Remove-branding add-on can be bought here: its checkout
  // link is deployment configuration, and the Billing page already gates
  // its card on the same answer. Open until the answer says otherwise, so
  // a configured deployment (the normal case) never flashes "not open".
  const [addonCheckoutOpen, setAddonCheckoutOpen] = useState(true);
  useEffect(() => {
    if (!paidLevel) return;
    let live = true;
    dashboardApi
      .getBilling()
      .then((res) => {
        if (live) setAddonCheckoutOpen(res.removeBadgeCheckout === true);
        const interval = res.subscription?.paymentFrequencyInterval ?? '';
        const known: Billing | null = /year/i.test(interval) ? 'yearly' : /month/i.test(interval) ? 'monthly' : null;
        if (!live || !known) return;
        setCycle(known);
        if (!choseBilling.current) setBilling(known);
      })
      .catch(() => undefined); // the marks without the cycle are still right
    return () => {
      live = false;
    };
  }, [paidLevel]);
  const current: CurrentPlan | undefined = paidLevel ? { plan: paidLevel, cycle } : undefined;

  return (
    <section class="site-section site-section-alt" id="pricing">
      <div class="site-wrap">
        <span class="site-eyebrow pc-eyebrow">Pricing</span>
        {/* The page's own heading, so an h1 — styled like the other
            sections' h2s so it looks the same as before. */}
        <h1 class="pc-page-title">Start free, then pick a plan that fits</h1>
        <p class="site-section-lead">Everything you need to put an AI support agent on your site. Cancel anytime.</p>

        <BillingToggle billing={billing} setBilling={chooseBilling} />
        <PlanCards plans={plans} billing={billing} current={current} />
        {/* True of every checkout: Dodo Payments, the merchant of record,
            adds any VAT or sales tax on top at payment. */}
        <p class="pc-tax-note">Prices in USD, excluding any applicable taxes.</p>

        {/* What running out of messages means, stated before anyone pays —
            server/src/billing/allowance.ts is what it describes. */}
        <p class="pc-limit-note">
          If your chatbots use up the month's messages, they stop answering and show your contact form instead, so you still get the
          lead. We email you at 80% and again at 100%, and they pick up again on the 1st of the month — or straight away if you upgrade.
        </p>

        {/* Signed in on Starter: the one paid extra their plan can take,
            right where the account menu's "Upgrade" lands them. Hidden for
            everyone else — the trial can't buy it, and Professional and
            Business already leave the badge off. */}
        {account?.limits.badge === 'addon' && (
          <div class="pc-addons">
            <h3 class="pv-pricing-title">Add-ons for your Starter plan</h3>
            <RemoveBadgeCard active={account.removeBadgeAddon} checkoutOpen={addonCheckoutOpen} />
          </div>
        )}

        <PricingFaq />

        {/* The same three facts as the top of /privacy — on every plan, so
            they belong under the cards rather than in any one of them. */}
        <div class="pv-pricing">
          <h3 class="pv-pricing-title">Privacy on every plan</h3>
          <PrivacyFacts />
          <p class="pv-pricing-link">
            <a href="/privacy">Read the privacy policy</a>
          </p>
        </div>
      </div>
    </section>
  );
}
